/** @jsxImportSource react */
'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { motion, useInView } from 'motion/react';

import { ArrowRight, Dot } from './icons';
import { SignupLink } from './signup-link';

const CLASSES = [
  'New member class',
  'Leader onboarding',
  'Small group leadership',
  'Baptism class',
];

export function Journey() {
  return (
    <section className="bg-[color:var(--color-ink)] py-20 text-white sm:py-28 lg:py-[180px]">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-14 px-5 sm:px-8 lg:gap-20 lg:px-20">
        {/* Top — case study */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-stretch lg:gap-20">
          <JourneyStory />
          <JourneyStats />
        </div>

        {/* Bottom — "Your church is next." close */}
        <div className="flex flex-col items-start justify-between gap-10 border-t border-[color:var(--color-ink-line)] pt-[60px] lg:flex-row lg:items-center">
          <motion.div
            className="flex flex-1 flex-col gap-5"
            initial={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ margin: '-100px', once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-[color:var(--color-highlight)]" />
              <span className="text-[13px] font-medium uppercase tracking-[0.14em] text-[color:var(--color-highlight)]">
                Now
              </span>
            </div>
            <h3 className="text-[clamp(40px,5.5vw,64px)] font-bold tracking-[-0.032em] [line-height:1.06] text-white">
              Your church is next.
            </h3>
          </motion.div>
          <motion.div
            className="flex max-w-[440px] flex-col items-start gap-6"
            initial={{ opacity: 0, y: 16 }}
            transition={{ delay: 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ margin: '-100px', once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <p className="text-[18px] leading-[1.66] text-[color:var(--color-gray-light)]">
              Same playbook Journey used. Same support. Free for 21 days.
            </p>
            <SignupLink
              className="group inline-flex items-center gap-3 rounded-[14px] bg-[color:var(--color-highlight)] px-9 py-[22px] text-[18px] font-semibold text-[color:var(--color-ink)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-10px_rgba(239,254,71,0.4)]"
              source="journey"
            >
              <span>Try OnArk free</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </SignupLink>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function JourneyStory() {
  return (
    <motion.div
      className="flex flex-col gap-10"
      initial={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ margin: '-100px', once: true }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-3">
        <div className="h-px w-8 bg-[color:var(--color-highlight)]" />
        <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-highlight)]">
          Where OnArk began
        </span>
      </div>

      {/* Journey Church logo serves as the section's customer identifier.
          The source SVG is black; we filter it to white for the dark section. */}
      <Image
        alt="Journey Church"
        className="h-10 w-auto self-start [filter:brightness(0)_invert(1)] sm:h-12"
        height={54}
        priority={false}
        src="/illustrations/journey-church.svg"
        width={240}
      />

      <div className="flex flex-col gap-3">
        <h2 className="text-[clamp(48px,7vw,72px)] font-bold tracking-[-0.032em] [line-height:1.06]">
          America&apos;s #1 fastest-growing church.
        </h2>
        <span className="pt-2 text-[18px] text-[color:var(--color-gray-med)]">
          Outreach Magazine&apos;s 2025 list. Built on OnArk.
        </span>
      </div>

      <p className="max-w-[560px] text-[19px] leading-[1.66] text-[color:var(--color-gray-light)]">
        OnArk was built for Journey first. Then we released it to every church.
        Years later they still run their most mission-critical pathways on it —
        new member class, leader onboarding, small group leadership, and baptism
        class.
      </p>

      <Link
        className="group inline-flex w-fit items-center gap-3 rounded-[14px] bg-[color:var(--color-highlight)] px-7 py-4 text-[16px] font-semibold text-[color:var(--color-ink)] transition-all hover:-translate-y-0.5"
        href="/story/journey-church"
      >
        <span>Read Journey&apos;s story</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
    </motion.div>
  );
}

function JourneyStats() {
  return (
    <motion.div
      className="flex flex-col gap-8 rounded-[24px] bg-[color:var(--color-ink-soft)] p-8 sm:p-10 lg:p-12"
      initial={{ opacity: 0, y: 24 }}
      transition={{ delay: 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ margin: '-100px', once: true }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-2">
        <Dot className="h-2 w-2 text-[color:var(--color-highlight)]" />
        <span className="text-[12px] font-medium uppercase tracking-[0.14em] text-[color:var(--color-highlight)]">
          On OnArk since 2020
        </span>
      </div>

      <div className="flex flex-col gap-3 border-b border-[color:var(--color-ink-line)] pb-7">
        <motion.span
          className="text-[clamp(48px,9vw,64px)] font-bold leading-none tracking-[-0.03em] text-[color:var(--color-highlight)]"
          initial={{ opacity: 0, scale: 0.92, y: 12 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ margin: '-100px', once: true }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
        >
          #1
        </motion.span>
        <span className="text-[15px] leading-[1.5] text-[color:var(--color-gray-light)]">
          Fastest-growing church in America, 2025.
        </span>
      </div>

      <div className="flex flex-col gap-4 border-b border-[color:var(--color-ink-line)] pb-7">
        <span className="text-[13px] font-medium uppercase tracking-[0.14em] text-[color:var(--color-gray-med)]">
          Running on OnArk
        </span>
        <ul className="flex flex-col gap-2.5">
          {CLASSES.map((label, i) => (
            <motion.li
              key={label}
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -8 }}
              transition={{
                delay: 0.35 + i * 0.08,
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
              viewport={{ margin: '-100px', once: true }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <Dot className="h-1.5 w-1.5 shrink-0 text-[color:var(--color-highlight)]" />
              <span className="text-[18px] font-medium tracking-[-0.005em] text-white">
                {label}
              </span>
            </motion.li>
          ))}
        </ul>
        <motion.p
          className="pt-2 text-[14px] leading-[1.55] text-[color:var(--color-gray-med)]"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.75, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ margin: '-100px', once: true }}
          whileInView={{ opacity: 1 }}
        >
          Four pathways that touch every new member, leader, baptism candidate,
          and small-group host at Journey.
        </motion.p>
      </div>

      <div className="flex flex-col gap-3">
        <Counter suffix="+" to={7000} />
        <span className="text-[15px] leading-[1.5] text-[color:var(--color-gray-light)]">
          Unique people through Journey&apos;s OnArk classes. And counting.
        </span>
      </div>
    </motion.div>
  );
}

function Counter({ suffix, to }: { to: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { margin: '-100px', once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1400;
    const start = performance.now();
    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);

      setDisplay(Math.round(to * easeOutQuart(t)));
      if (t < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);

    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span
      ref={ref}
      className="text-[clamp(48px,9vw,64px)] font-bold leading-none tracking-[-0.03em] text-white tabular-nums"
    >
      {display.toLocaleString()}
      {suffix ?? ''}
    </span>
  );
}
